<template>
  <div class="body">
    <w-header :is-logo-only="true" />
    <main role="main" class="connection-main">
      <section class="connection-box">
        <connection-wavve
          v-if="isAccountRequired"
          :credential="userCredential"
          @create-account="successCreateAccount"
          @click-cancel-button="onMoveReturnUrl"
        />
        <connection-sns
          v-else-if="wavveId"
          :credential="userCredential"
          :sns-list="snsList"
          :wavve-id="wavveId"
          @change-connection="onChangeConnect"
          @click-confirm-button="onMoveReturnUrl"
        />
        <loading-bar v-else />
      </section>
    </main>
  </div>
</template>

<script setup>
  import WHeader from '@/components/header/WHeader'
  import ConnectionSns from '@/components/connection/sns'
  import ConnectionWavve from '@/components/connection/wavve'
  import LoadingBar from '@/components/ui/LoadingBar/LoadingBar'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router/composables'
  import { getConnections } from '@/common/requests/data-fetch'
  import cookie from '@/common/utils/cookie'
  import constant from '@/common/resources/constant'
  import { removeProperties } from '@/common/utils/common'

  const route = useRoute()
  const router = useRouter()
  const isAccountRequired = ref(false)
  const wavveId = ref()
  const snsList = ref([])
  const isLoading = ref(false)

  const userCredential = computed(() => {
    /**
     * NOTE: credential은 url의 파라미터로 전달 된다.
     */
    const { credential } = route.query
    return credential
  })

  const getConnectionInfo = async () => {
    const token = userCredential.value
    if (!token) {
      alert('필수 정보가 누락되었습니다. 이전 화면으로 이동합니다.')
      history.back()
      return
    }
    isLoading.value = true
    const response = await getConnections({
      token,
    })
    isLoading.value = false
    if (response.status !== 200) {
      const { error } = response
      if (error.data?.code === '400.019') {
        isAccountRequired.value = true
        return
      }
      alert('일시적으로 처리하지 못했습니다. 다시 시도해주세요.')
      return
    }
    const { data = {} } = response
    isAccountRequired.value = false
    wavveId.value = data.wavveId
    snsList.value = data.snsList
  }

  const successCreateAccount = async () => {
    await getConnectionInfo()
  }

  const onChangeConnect = async () => {
    await getConnectionInfo()

    if (!route.query?.type) {
      return
    }
    const resultQuery = removeProperties(route.query, ['token', 'type'])
    router.replace({
      query: resultQuery,
    })
  }

  const onMoveReturnUrl = () => {
    const returnUrl = cookie.getItem('wavve-connection-return-url')
    removeReturnUrlInCookie()
    if (returnUrl) {
      isLoading.value = true
      location.href = returnUrl
      return
    }
    router.push('/')
  }

  const setReturnUrlInCookie = () => {
    /**
     * NOTE: member에서 sns연동관리 화면으로 이동시킬 때 return_url을 파라미터로 전달한다.
     * (return_url용도: 뒤로가기 동작 시 return_url로 이동)
     * member의 회원정보 수정화면과 sns연동화면을 왔다갔다 하게 하기 위함
     */
    const { return_url: returnUrl } = route.query
    if (returnUrl) {
      cookie.setItem(
        'wavve-connection-return-url',
        returnUrl,
        null,
        constant.URL_COOKIE_DOMAIN,
        '/',
      )
    }
  }

  const removeReturnUrlInQuery = () => {
    if (!route.query?.return_url) {
      return
    }
    const resultQuery = removeProperties(route.query, ['return_url'])
    router.replace({
      query: resultQuery,
    })
  }

  const removeReturnUrlInCookie = () => {
    cookie.removeItem(
      'wavve-connection-return-url',
      constant.URL_COOKIE_DOMAIN,
      '/',
    )
  }

  onMounted(() => {
    /**
     * NOTE: return url을 cookie에 저장하는 이유 -
     * 특정 sns 로그인을 위한 callback url 생성 시 요청 헤더가 너무 길어져서
     * return_url 정보를 cookie에 저장하고 url에서 제거한다.
     */
    setReturnUrlInCookie()
    removeReturnUrlInQuery()
    getConnectionInfo()
  })
</script>

<style lang="scss" scoped>
  .body {
    height: 100%;
    min-height: inherit;
    min-width: 1240px;
  }
  .connection-main {
    padding: 80px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    .connection-box {
      padding: 40px;
      box-sizing: content-box;
      background-color: #252525;
    }
  }
</style>
